import { useState } from "react";

import { PropallyApiFactory } from "@/module/CustomApiFactory";
import { extractHttpStatus } from "@/utilities/extractHttpStatus";

// お断り API の結果。失敗は「再試行で回復するか」が違うため、呼び出し側が出し分けられるよう理由を分ける
//   not_found:     既に表示対象外（404。お断り済み・提携終了・削除済みなど）。再試行しても回復しない
//   not_refusable: お断りできない状態（422。マッチ済みなど）。API の message があれば添える
//   error:         一時エラー（通信断・5xx など status で判別できない失敗）。再試行を促す
type RefuseInvestmentProposalFailure = { ok: false; reason: "not_found" | "not_refusable" | "error"; message?: string };
export type RefuseInvestmentProposalResult = { ok: true } | RefuseInvestmentProposalFailure;

interface UseRefuseInvestmentProposal {
  // メッセージの送信元プランナーをお断りする
  refuseInvestmentProposal: (id: number) => Promise<RefuseInvestmentProposalResult>;
  loading: boolean;
}

// 422 の message は API が文字列で返したときだけ採用する（それ以外の形は呼び出し側の既定文言に任せる）
const extractResponseMessage = (error: unknown): string | undefined => {
  const message = (error as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  return typeof message === "string" ? message : undefined;
};

const toFailure = (error: unknown): RefuseInvestmentProposalFailure => {
  switch (extractHttpStatus(error)) {
    case 404:
      return { ok: false, reason: "not_found" };
    case 422:
      return { ok: false, reason: "not_refusable", message: extractResponseMessage(error) };
    default:
      return { ok: false, reason: "error" };
  }
};

// メッセージ詳細の「お断り」（Trello #224）。お断りの単位はプランナーで、以後そのプランナーからの
// メッセージ（過去分も含む）は一覧・詳細・未読件数・通知に出なくなる。解除はできない
export const useRefuseInvestmentProposal = (): UseRefuseInvestmentProposal => {
  const [loading, setLoading] = useState(false);
  const fp = PropallyApiFactory();

  const refuseInvestmentProposal = async (id: number): Promise<RefuseInvestmentProposalResult> => {
    setLoading(true);
    try {
      const res = await fp.v1InvestmentProposalsInvestmentProposalIdRefusalPost(id, { withCredentials: true });
      return res.status === 200 ? { ok: true } : { ok: false, reason: "error" };
    } catch (e) {
      const failure = toFailure(e);
      // 404 / 422 は画面で扱う想定内の結果なのでログに出さない
      // （HTTP エラーの報告は CustomApiFactory に集約されており、console.error は CaptureConsole 経由で二重送信になる）
      if (failure.reason === "error") console.error(e);
      return failure;
    } finally {
      setLoading(false);
    }
  };

  return { refuseInvestmentProposal, loading };
};
